import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { Check, Star, Zap, Rocket, Crown, TrendingUp } from 'lucide-react';
import { useCurrency, formatPrice } from '../hooks/useCurrency';
import { trackWhatsAppClick } from '../hooks/useMetaPixel';
interface Plan {
  name: string;
  subtitle: string;
  price: number;
  originalPrice?: number;
  monthlyPrice?: number;
  currency: string;
  badge?: string;
  badgeColor?: string;
  description: string;
  features: string[];
  priceBreakdown?: string[];
  cta: string;
  highlighted: boolean;
  paymentLabel: string;
  promoLabel?: string;
  whatsappMessage: string;
}
const WA_BASE = 'https://wa.me/50685316690';
const plans: Plan[] = [{
  name: 'Menú Digital',
  subtitle: 'La presencia digital de tu restaurante',
  price: 200,
  originalPrice: 400,
  currency: 'USD',
  promoLabel: 'Oferta de lanzamiento',
  description: 'Un menú digital funcional diseñado para mostrar tu menú, ambiente y convertir visitas en reservaciones por WhatsApp.',
  features: ['Diseño premium ultra creativo personalizado', 'Copy persuasivo profesional', 'Dominio propio incluido (1 año)', 'Conexión directa con WhatsApp', 'Diseño responsive optimizado', '5 imágenes DTC optimizadas para Meta Andromeda', 'Entrega en 5 días hábiles', '2 rondas de revisión', 'Soporte por 30 días'],
  cta: 'Quiero Mi Menú Digital',
  highlighted: false,
  paymentLabel: 'Pago único',
  whatsappMessage: 'Hola, estoy interesado(a) en el plan *Menú Digital* ($200 USD - oferta de lanzamiento) para mi restaurante. Me gustaría más información.'
}, {
  name: 'Menú Digital + Meta Ads',
  subtitle: 'Lanzá tu restaurante con todo',
  price: 300,
  originalPrice: 600,
  currency: 'USD',
  badge: 'POPULAR',
  badgeColor: 'from-cyan-400 to-blue-500',
  promoLabel: 'Oferta de lanzamiento',
  description: 'Tu menú digital funcional + configuración completa de campaña en Meta Ads + 10 creativos publicitarios para rotar. Vos decidís cuánto invertir en pauta.',
  features: ['Todo lo del plan Menú Digital', 'Configuración profesional de campaña Meta Ads', '10 imágenes DTC para rotar en tus campañas', 'Segmentación de clientes en tu zona', 'Creativos optimizados para Meta Andromeda', 'Monitoreo de campaña por 1 mes', 'Reporte de resultados', 'Pauta en Meta a tu elección (vos decidís el monto)'],
  priceBreakdown: ['$200 — Menú digital funcional', '$100 — Configuración de campaña Meta Ads', '+ Pauta en Meta aparte (a tu elección)'],
  cta: 'Llenar Mis Mesas',
  highlighted: true,
  paymentLabel: 'Pago único',
  whatsappMessage: 'Hola, estoy interesado(a) en el plan *Menú Digital + Meta Ads* ($300 USD - oferta de lanzamiento) para mi restaurante. Me gustaría más información.'
}, {
  name: 'Crecimiento Continuo',
  subtitle: 'Más clientes cada mes',
  price: 300,
  originalPrice: 600,
  monthlyPrice: 158,
  currency: 'USD',
  badge: 'MEJOR VALOR',
  badgeColor: 'from-yellow-400 to-orange-500',
  promoLabel: 'Oferta de lanzamiento',
  description: 'El paquete completo: menú digital + campaña + creativos que nosotros rotamos estratégicamente según las métricas. Vos decidís cuánto pautar cada mes.',
  features: ['Todo lo del plan Menú Digital + Meta Ads', 'Mantenimiento mensual del menú digital', 'Optimización continua de campaña', 'Rotación estratégica de creativos según métricas', 'Nuevos diseños DTC cada mes según rendimiento', 'Pauta mensual en Meta a tu elección', 'Reportes mensuales de clientes captados', 'Ajustes estratégicos basados en datos', 'Soporte prioritario continuo', 'Contrato de 6 meses (renovable)'],
  priceBreakdown: ['Mes 1: $300 (menú digital + campaña inicial)', 'Desde mes 2: $158/mes (gestión, optimización y creativos)', '+ Pauta en Meta aparte (a tu elección)', 'Contrato mínimo: 6 meses', 'Renovación o rescisión cada 6 meses'],
  cta: 'Crecer Sin Límites',
  highlighted: false,
  paymentLabel: 'Mes 1',
  whatsappMessage: 'Hola, estoy interesado(a) en el plan *Crecimiento Continuo* ($300 USD mes 1, luego $158/mes + pauta - contrato 6 meses - oferta de lanzamiento) para mi restaurante. Me gustaría más información.'
}];
const cardIcons = [Zap, Rocket, TrendingUp];
export function PricingSection() {
  const currency = useCurrency();
  const isUSD = currency.code === 'USD';
  return <section id="precios" className="py-24 bg-[#0a0a1a] relative overflow-hidden" data-id="element-236">
      {/* Background Glows */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-600/15 rounded-full blur-[120px]" data-id="element-237" />
      <div className="absolute top-1/4 right-0 w-[400px] h-[400px] bg-pink-600/10 rounded-full blur-[100px]" data-id="element-238" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-239">
        <div className="text-center mb-16" data-id="element-240">
          <h2 className="text-3xl md:text-5xl font-bold font-orbitron mb-4" data-id="element-241">
            Tu Inversión para{' '}
            <span className="text-gradient-cosmic" data-id="element-242">Llenar Tus Mesas</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg" data-id="element-243">
            Un solo producto, tres formas de atraer más comensales a tu
            restaurante. Elegí el nivel que mejor se adapte a tus objetivos.
          </p>
          {!currency.loading && currency.countryName && !isUSD && <p className="text-purple-400/70 text-sm mt-3" data-id="element-244">
              💱 Precios convertidos a {currency.code} para{' '}
              {currency.countryName} (ref. aprox.)
            </p>}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch" data-id="element-245">
          {plans.map((plan, index) => {
          const Icon = cardIcons[index];
          return <motion.div key={plan.name} initial={{
            opacity: 0,
            y: 40
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.15
          }} className={`relative flex flex-col ${plan.highlighted ? 'lg:-mt-4 lg:mb-[-16px]' : ''}`} data-id="element-246">
                {/* Glow border for highlighted */}
                {plan.highlighted && <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 via-pink-600 to-yellow-500 rounded-3xl blur opacity-60 animate-pulse-glow" data-id="element-247" />}

                <div className={`relative flex flex-col flex-1 rounded-3xl p-8 border transition-all duration-300 ${plan.highlighted ? 'bg-[#050510] border-white/20 shadow-2xl' : 'bg-[#050510]/80 border-white/10 hover:border-purple-500/30'}`} data-id="element-248">
                  {/* Badge */}
                  {plan.badge && <div className="absolute top-0 right-0 p-4" data-id="element-249">
                      <div className={`bg-gradient-to-r ${plan.badgeColor} text-black text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1`} data-id="element-250">
                        <Star className="w-3 h-3 fill-black" data-id="element-251" />
                        {plan.badge}
                      </div>
                    </div>}

                  {/* Icon & Name */}
                  <div className="mb-6" data-id="element-252">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${plan.highlighted ? 'bg-gradient-to-br from-purple-500/20 to-pink-500/20' : 'bg-white/5'}`} data-id="element-253">
                      <Icon className={`w-6 h-6 ${index === 0 ? 'text-cyan-400' : index === 1 ? 'text-purple-400' : 'text-yellow-400'}`} data-id="element-254" />
                    </div>
                    <h3 className="text-2xl font-bold text-white font-orbitron mb-1" data-id="element-255">
                      {plan.name}
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-256">{plan.subtitle}</p>
                  </div>

                  {/* Price */}
                  <div className="mb-6" data-id="element-257">
                    {plan.originalPrice && <div className="flex items-center gap-3 mb-1" data-id="element-258">
                        <span className="text-2xl text-gray-600 line-through font-orbitron" data-id="element-259">
                          {currency.loading ? `$${plan.originalPrice.toLocaleString()}` : formatPrice(plan.originalPrice, currency.rate, currency.symbol)}
                        </span>
                        {plan.promoLabel && <span className="inline-flex items-center gap-1 text-xs font-bold text-green-400 bg-green-400/10 px-2 py-1 rounded-full" data-id="element-260">
                            🚀 {plan.promoLabel}
                          </span>}
                      </div>}
                    <div className="flex items-baseline gap-2" data-id="element-261">
                      <span className={`text-4xl md:text-5xl font-bold font-orbitron ${plan.highlighted ? 'text-gradient-cosmic' : 'text-white'}`} data-id="element-262">
                        {currency.loading ? `$${plan.price}` : formatPrice(plan.price, currency.rate, currency.symbol)}
                      </span>
                      <span className="text-lg text-gray-400" data-id="element-263">
                        {currency.loading ? 'USD' : currency.code}
                      </span>
                    </div>

                    {/* Monthly price for recurring plan */}
                    {plan.monthlyPrice && <div className="flex items-baseline gap-2 mt-2" data-id="element-264">
                        <span className="text-lg text-purple-400 font-orbitron font-bold" data-id="element-265">
                          +{' '}
                          {currency.loading ? `$${plan.monthlyPrice}` : formatPrice(plan.monthlyPrice, currency.rate, currency.symbol)}
                        </span>
                        <span className="text-sm text-gray-500" data-id="element-266">
                          /mes desde el mes 2
                        </span>
                      </div>}

                    {!isUSD && !currency.loading && <p className="text-xs text-gray-500 mt-1" data-id="element-267">
                        ~${plan.price} USD • {plan.paymentLabel}
                      </p>}
                    {(isUSD || currency.loading) && <p className="text-sm text-gray-500 mt-1" data-id="element-268">
                        {plan.paymentLabel}
                      </p>}
                  </div>

                  {/* Description */}
                  <p className="text-gray-400 text-sm leading-relaxed mb-6 border-b border-white/5 pb-6" data-id="element-269">
                    {plan.description}
                  </p>

                  {/* Price Breakdown */}
                  {plan.priceBreakdown && <div className="mb-6 bg-white/5 rounded-xl p-4 border border-white/5" data-id="element-270">
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-271">
                        Desglose de inversión:
                      </p>
                      {plan.priceBreakdown.map((item, i) => <p key={i} className="text-sm text-gray-400 py-1" data-id="element-272">
                          {item}
                        </p>)}
                    </div>}

                  {/* Features */}
                  <div className="space-y-3 mb-8 flex-1" data-id="element-273">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4" data-id="element-274">
                      Qué incluye:
                    </p>
                    {plan.features.map((feature, i) => <div key={i} className="flex items-start gap-3" data-id="element-275">
                        <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${plan.highlighted ? 'bg-green-500/20' : 'bg-white/5'}`} data-id="element-276">
                          <Check className={`w-3 h-3 ${plan.highlighted ? 'text-green-400' : 'text-gray-500'}`} data-id="element-277" />
                        </div>
                        <span className="text-sm text-gray-300" data-id="element-278">{feature}</span>
                      </div>)}
                  </div>

                  {/* CTA */}
                  <div className="mt-auto space-y-3" data-id="element-279">
                    <a href={`${WA_BASE}?text=${encodeURIComponent(plan.whatsappMessage)}`} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsAppClick()} className={`block w-full py-4 rounded-xl font-bold text-center text-lg transition-all duration-300 transform hover:scale-[1.02] ${plan.highlighted ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-[0_0_30px_rgba(217,70,239,0.5)]' : 'bg-white/5 text-white border border-white/10 hover:bg-white/10 hover:border-purple-500/30'}`} data-id="element-280">
                      {plan.cta}
                    </a>
                    <p className="text-center text-xs text-gray-600" data-id="element-281">
                      {plan.highlighted ? 'Garantía de satisfacción • Pago seguro' : 'Sin compromiso • Pago seguro'}
                    </p>
                  </div>
                </div>
              </motion.div>;
        })}
        </div>

        {/* Trust note */}
        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.6
      }} className="text-center text-gray-600 text-sm mt-12" data-id="element-282">
          Todos los planes incluyen consulta gratuita inicial • Aceptamos
          transferencia bancaria (BAC, Popular, Nacional) y SINPE Móvil
        </motion.p>
      </div>
    </section>;
}